import type { RatioAnalysisResponse, ServerApiClient } from '../../../data/ServerApiClient'

// Process-wide cache for the ratio library so the guided-analysis modal can
// embed the full ratio card instantly instead of refetching on every click.
// Prefetched when the CFO surface loads; the modal reads through the same cache.
// Caches the in-flight promise (dedupes concurrent reads) and drops failures so
// a transient 500 doesn't poison the entry.

const trim = (value: unknown): string => String(value ?? '').trim()
const normalizeAsOf = (asOf?: string | null): string | null => {
  const m = /^(\d{4})-(\d{2})/.exec(trim(asOf))
  return m ? `${m[1]}-${m[2]}-01` : null
}
const keyOf = (workbookId: string, asOf?: string | null): string =>
  `${workbookId}::${normalizeAsOf(asOf) || 'latest'}`

const ratiosCache = new Map<string, Promise<RatioAnalysisResponse>>()

export function loadCfoRatiosCompatible(
  appApi: ServerApiClient,
  workbookId: string,
  opts: { grain: 'month'; asOf?: string },
): Promise<RatioAnalysisResponse> {
  const load = (appApi.getCfoRatios || appApi.getQuickBooksRatios)?.bind(appApi)
  if (typeof load !== 'function') return Promise.reject(new Error('Ratio reporting contract unavailable'))
  return load(workbookId, opts)
}

/** Fetch the ratio library, returning a cached (possibly prewarmed) result. */
export function getCachedQuickBooksRatios(
  appApi: ServerApiClient,
  workbookId: string,
  asOf?: string | null,
): Promise<RatioAnalysisResponse> {
  const wb = trim(workbookId)
  const key = keyOf(wb, asOf)
  const existing = ratiosCache.get(key)
  if (existing) return existing
  const normalized = normalizeAsOf(asOf)
  const opts = normalized ? { grain: 'month' as const, asOf: normalized } : { grain: 'month' as const }
  const promise = loadCfoRatiosCompatible(appApi, wb, opts)
  ratiosCache.set(key, promise)
  promise.catch(() => {
    // Don't cache a failure — let the next read retry.
    if (ratiosCache.get(key) === promise) ratiosCache.delete(key)
  })
  return promise
}

/** Fire-and-forget warm of the ratio library (call on CFO surface load). */
export function prefetchQuickBooksRatios(
  appApi: ServerApiClient | null | undefined,
  workbookId: string | null | undefined,
  asOf?: string | null,
): void {
  const wb = trim(workbookId)
  if (!wb || !appApi || (typeof appApi.getCfoRatios !== 'function' && typeof appApi.getQuickBooksRatios !== 'function')) return
  void getCachedQuickBooksRatios(appApi, wb, asOf).catch(() => {})
}
